/*
 * Logo Pong Trigger — Level 2 Alpine component
 *
 * Attached to the Pembleton logo link in the site header. A sustained press
 * (`HOLD_DURATION` ms) on the logo dispatches a `pong:reveal` window event
 * with the logo's viewport-center coordinates; app.js listens for that event,
 * sets `--pong-origin-x/y` CSS custom properties, adds `pong-overlay--revealing`
 * to the overlay, and opens the pong store. A CSS keyframe animates a
 * circular clip-path reveal from the logo outward.
 *
 * Progressive feedback: during the press, an inner `.logo-pong-target` element
 * (the SVG's wrapping span) is nudged with a random `translate` each frame,
 * with amplitude ramping as `progress²` so the vibration starts subtle and
 * escalates visibly before the trigger fires. This is the only cue the user
 * gets that something is happening, so it must be felt immediately.
 *
 * Click semantics:
 * - Quick taps (< `NAV_SUPPRESS_THRESHOLD` ms) navigate home, as normal.
 * - Long-but-incomplete presses cancel cleanly — the trailing click is
 *   suppressed so the user doesn't get teleported to `/` after a near-miss.
 * - A completed trigger also suppresses the click.
 */

const HOLD_DURATION = 3000;
const NAV_SUPPRESS_THRESHOLD = 300;
const MAX_AMPLITUDE_PX = 6;
const MAX_SCALE_SWELL = 0.08;

export default function logoPongTrigger() {
    return {
        _pressStart: 0,
        _rafId: null,
        _target: null,
        _suppressClick: false,

        init() {
            this._target = this.$el.querySelector('.logo-pong-target') || this.$el;
        },

        destroy() {
            this._reset();
        },

        press(event) {
            if (event.button !== undefined && event.button !== 0) {
                return;
            }
            if (this.$store.pong.active) {
                return;
            }

            this._pressStart = performance.now();
            this._suppressClick = false;
            this._tick();
        },

        release() {
            if (!this._pressStart) {
                return;
            }
            const held = performance.now() - this._pressStart;
            if (held > NAV_SUPPRESS_THRESHOLD) {
                this._suppressClick = true;
            }
            this._reset();
        },

        cancel() {
            if (!this._pressStart) {
                return;
            }
            this._suppressClick = true;
            this._reset();
        },

        onClick(event) {
            if (this._suppressClick) {
                event.preventDefault();
                event.stopPropagation();
                this._suppressClick = false;
            }
        },

        _tick() {
            const now = performance.now();
            const elapsed = now - this._pressStart;
            const progress = Math.min(elapsed / HOLD_DURATION, 1);

            const ramp = progress * progress;
            const amp = MAX_AMPLITUDE_PX * ramp;
            const dx = (Math.random() * 2 - 1) * amp;
            const dy = (Math.random() * 2 - 1) * amp;
            const scale = 1 + MAX_SCALE_SWELL * ramp;

            if (this._target) {
                this._target.style.translate = `${dx}px ${dy}px`;
                this._target.style.scale = String(scale);
            }

            if (progress >= 1) {
                this._trigger();
                return;
            }

            this._rafId = requestAnimationFrame(() => this._tick());
        },

        _trigger() {
            this._suppressClick = true;

            const rect = this.$el.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;

            window.dispatchEvent(new CustomEvent('pong:reveal', {
                detail: { x: cx, y: cy },
            }));

            this._reset();
        },

        _reset() {
            if (this._rafId) {
                cancelAnimationFrame(this._rafId);
            }
            this._rafId = null;
            this._pressStart = 0;
            if (this._target) {
                this._target.style.translate = '';
                this._target.style.scale = '';
            }
        },
    };
}
